import { AnimatePresence, motion } from 'motion/react';
import { Plus, Save, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { SavingsGoal } from '../../lib/types';

interface SavingsGoalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingGoal?: SavingsGoal | null;
  onAddGoal: (goal: Omit<SavingsGoal, 'id' | 'uid'>) => Promise<void>;
  onUpdateGoal: (id: string, data: Partial<SavingsGoal>) => Promise<void>;
  currency: string;
}

export function SavingsGoalsModal({ isOpen, onClose, editingGoal, onAddGoal, onUpdateGoal }: SavingsGoalsModalProps) {
  const [newGoal, setNewGoal] = useState({ name: '', targetAmount: '', currentAmount: '' });

  useEffect(() => {
    if (isOpen) {
      if (editingGoal) {
        setNewGoal({
          name: editingGoal.name,
          targetAmount: editingGoal.targetAmount.toString(),
          currentAmount: editingGoal.currentAmount.toString()
        });
      } else {
        setNewGoal({ name: '', targetAmount: '', currentAmount: '' });
      }
    }
  }, [isOpen, editingGoal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGoal.name || !newGoal.targetAmount) return;
    
    const goalData = {
      name: newGoal.name,
      targetAmount: parseFloat(newGoal.targetAmount) || 0,
      currentAmount: parseFloat(newGoal.currentAmount) || 0
    };

    if (editingGoal) {
      await onUpdateGoal(editingGoal.id, goalData);
    } else {
      await onAddGoal(goalData);
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-2xl bg-white dark:bg-black border border-zinc-100 dark:border-zinc-900 rounded-[32px] shadow-2xl overflow-hidden transition-colors flex flex-col max-h-[80vh]"
          >
            <div className="p-6 border-b border-zinc-100 dark:border-zinc-900 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                  {editingGoal ? 'Edit Goal' : 'Add Savings Goal'}
                </h2>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Goal Name</label>
                  <input 
                    required 
                    placeholder="e.g. New Laptop" 
                    className="w-full px-4 py-2.5 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-900 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white"
                    value={newGoal.name}
                    onChange={e => setNewGoal({...newGoal, name: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Target</label>
                  <input 
                    type="number" 
                    placeholder="0.00" 
                    step="0.01"
                    required
                    className="w-full px-4 py-2.5 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-900 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white"
                    value={newGoal.targetAmount}
                    onChange={e => setNewGoal({...newGoal, targetAmount: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Current</label>
                  <input 
                    type="number" 
                    placeholder="0.00" 
                    step="0.01"
                    className="w-full px-4 py-2.5 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-900 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white"
                    value={newGoal.currentAmount}
                    onChange={e => setNewGoal({...newGoal, currentAmount: e.target.value})}
                  />
                </div>
                <button 
                  type="submit" 
                  className="w-full h-[48px] mt-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-2xl font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-lg active:scale-[0.98]"
                >
                  {editingGoal ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  {editingGoal ? 'Update Goal' : 'Add Goal'}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}